import newRelicAgent from '../../../newRelic';
import { Logger, nullLogger } from './Logger';
import storageMonitoringService from './StorageMonitoringService';

export enum StorageErrorType {
	QUOTA_EXCEEDED = 'QuotaExceededError',
	CHUNK_LOAD_ERROR = 'ChunkLoadError',
	DATABASE_CLOSING = 'DatabaseClosingError',
	INTERNAL_ERROR = 'InternalError',
	INVALID_STATE = 'InvalidStateError',
	ABORT_ERROR = 'AbortError',
	UNKNOWN_ERROR = 'UnknownError'
}

export interface StorageErrorDetails {
	type: StorageErrorType;
	originalError: Error;
	context: string;
	storageUsagePercentage?: number;
	storageUsage?: number;
	storageQuota?: number;
	additionalInfo?: Record<string, unknown>;
}

/**
 * Utility class for tracking and reporting storage-related errors to New Relic
 *
 * Handles common storage errors:
 * - QuotaExceededError: Storage quota exceeded
 * - ChunkLoadError: Failed to load code-split chunks (often due to service worker cache issues)
 * - DatabaseClosingError: IndexedDB connection closing
 * - InvalidStateError: Database connection in invalid state
 */
class StorageErrorTracker {
	private logger: Logger = process.env.NODE_ENV === 'production' ? nullLogger : console;

	/**
	 * Identify the type of storage error
	 */
	private identifyErrorType( error: Error ): StorageErrorType {
		const errorMessage = error.message.toLowerCase();
		const errorName = error.name.toLowerCase();

		// QuotaExceededError
		if ( errorName.includes( 'quotaexceedederror' ) || errorMessage.includes( 'quota' ) ) {
			return StorageErrorType.QUOTA_EXCEEDED;
		}

		// ChunkLoadError (Webpack)
		if ( errorMessage.includes( 'loading chunk' ) || errorMessage.includes( 'chunkloaderror' ) ) {
			return StorageErrorType.CHUNK_LOAD_ERROR;
		}

		// Database closing error
		if ( errorMessage.includes( 'database connection is closing' ) || errorMessage.includes( 'connection is closing' ) ) {
			return StorageErrorType.DATABASE_CLOSING;
		}

		// InvalidStateError
		if ( errorName.includes( 'invalidstateerror' ) ) {
			return StorageErrorType.INVALID_STATE;
		}

		// AbortError
		if ( errorName.includes( 'aborterror' ) ) {
			return StorageErrorType.ABORT_ERROR;
		}

		// Internal IndexedDB error
		if ( errorMessage.includes( 'internal error' ) && errorMessage.includes( 'indexed' ) ) {
			return StorageErrorType.INTERNAL_ERROR;
		}

		return StorageErrorType.UNKNOWN_ERROR;
	}

	async trackError(
		error: Error,
		context: string,
		additionalInfo?: Record<string, unknown>
	): Promise<StorageErrorDetails> {
		this.logger.log( 'trackError', error, context, additionalInfo );
		const errorType = this.identifyErrorType( error );
		const currentStatus = storageMonitoringService.getCurrentStatus();

		const errorDetails: StorageErrorDetails = {
			type: errorType,
			originalError: error,
			context,
			storageUsagePercentage: currentStatus?.estimate.usagePercentage,
			storageUsage: currentStatus?.estimate.usage,
			storageQuota: currentStatus?.estimate.quota,
			additionalInfo
		};

		this.reportToNewRelic( errorDetails );

		this.logger.error( `[Storage Error - ${errorType}]`, {
			context,
			error,
			storageUsage: `${currentStatus?.estimate.usagePercentage.toFixed( 2 )}%`,
			...additionalInfo
		} );

		if ( errorType === StorageErrorType.QUOTA_EXCEEDED ) {
			await storageMonitoringService.checkQuota();
		}

		return errorDetails;
	}

	private reportToNewRelic( details: StorageErrorDetails ): void {
		if ( !newRelicAgent ) { return; }

		try {
			newRelicAgent.noticeError( details.originalError, {
				errorType: details.type,
				context: details.context,
				storageUsagePercentage: details.storageUsagePercentage,
				storageUsage: details.storageUsage,
				storageQuota: details.storageQuota,
				...details.additionalInfo
			} );

			newRelicAgent.addPageAction( 'StorageError', {
				errorType: details.type,
				errorMessage: details.originalError.message,
				context: details.context,
				storageUsagePercentage: details.storageUsagePercentage,
				usageMB: details.storageUsage ? ( details.storageUsage / ( 1024 * 1024 ) ).toFixed( 2 ) : null,
				quotaMB: details.storageQuota ? ( details.storageQuota / ( 1024 * 1024 ) ).toFixed( 2 ) : null
			} );
		} catch ( error ) {
			this.logger.error( 'Failed to report storage error to New Relic:', error );
		}
	}

	/**
	 * Wrap a storage operation with error tracking
	 */
	async wrapStorageOperation<T>(
		operation: () => Promise<T>,
		context: string,
		additionalInfo?: Record<string, unknown>
	): Promise<T> {
		try {
			return await operation();
		} catch ( error ) {
			await this.trackError( error as Error, context, additionalInfo );
			throw error;
		}
	}

	/**
	 * Wrap IndexedDB operations with error tracking
	 */
	wrapIndexedDBOperation<T>(
		operation: () => Promise<T>,
		dbName: string,
		operationType: 'read' | 'write' | 'delete'
	): Promise<T> {
		return this.wrapStorageOperation(
			operation,
			'IndexedDB Operation',
			{ dbName, operationType }
		);
	}

	/**
	 * Wrap Cache API operations with error tracking
	 */
	wrapCacheOperation<T>(
		operation: () => Promise<T>,
		cacheName: string,
		operationType: 'put' | 'match' | 'delete'
	): Promise<T> {
		return this.wrapStorageOperation(
			operation,
			'Cache API Operation',
			{ cacheName, operationType }
		);
	}

	/**
	 * Track chunk loading errors
	 */
	trackChunkLoadError( chunkId: string | number, error: Error ): void {
		this.trackError( error, 'Chunk Loading', {
			chunkId,
			errorName: error.name,
			errorMessage: error.message
		} );
	}
}

export const storageErrorTracker = new StorageErrorTracker();
export default storageErrorTracker;
